import { Fragment, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import PipelineSteps, { PipelineStep } from './PipelineSteps';
import './taskPipelineModal.less';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskPipelineInsight {
  label: string;
  text: string;
}

export type TaskPipelineProgressMode = 'auto' | 'stepped' | 'manual';

export interface TaskPipelineModalProps {
  open: boolean;
  title: string;
  eyebrow?: string;
  steps: PipelineStep[];
  stepDurations?: number[];
  currentStep?: number;
  error?: { step: number; message: string };
  insights?: TaskPipelineInsight[];
  insightInterval?: number;
  progressMode?: TaskPipelineProgressMode;
  percent?: number;
  ringSize?: number;
  ringLabel?: string;
  onComplete?: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
  zIndex?: number;
}

export type { PipelineStep };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/** Wraps `*…*` segments in an italic serif `<em>`. */
const renderTitle = (title: string): ReactNode => {
  const parts = title.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i} className="tpm-title__serif">{part.slice(1, -1)}</em>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
};

const deriveEyebrow = (title: string): string => {
  const firstWord = title.replace(/\*[^*]+\*/g, '').trim().split(/\s+/)[0] ?? '';
  return firstWord.toLowerCase().endsWith('ing') ? firstWord : 'Working';
};

// ─── TaskPipelineModal ────────────────────────────────────────────────────────

const TaskPipelineModal = ({
  open,
  title,
  eyebrow,
  steps,
  stepDurations,
  currentStep,
  error,
  insights = [],
  insightInterval = 3200,
  progressMode,
  percent,
  ringSize = 160,
  ringLabel = 'Complete',
  onComplete,
  onCancel,
  cancelLabel = 'Cancel',
  zIndex = 9999,
}: TaskPipelineModalProps) => {
  const reduced = useMemo(prefersReducedMotion, []);
  const titleId = useId();

  const isControlled = typeof currentStep === 'number';
  const resolvedMode: TaskPipelineProgressMode =
    progressMode ?? (stepDurations && stepDurations.length > 0 ? 'auto' : 'stepped');

  const [internalStep, setInternalStep] = useState(0);
  const [autoPercent, setAutoPercent] = useState(0);
  const [insightIndex, setInsightIndex] = useState(0);
  const [pulse, setPulse] = useState(0);
  const lastAnnouncedRef = useRef<number>(0);
  const completedRef = useRef(false);

  const activeStep = isControlled ? (currentStep as number) : internalStep;
  const total = steps.length;

  // Reset when modal opens.
  useEffect(() => {
    if (!open) return;
    setInternalStep(0);
    setAutoPercent(0);
    setInsightIndex(0);
    setPulse((p) => p + 1);
    lastAnnouncedRef.current = 0;
    completedRef.current = false;
  }, [open]);

  // Auto-advance via stepDurations (uncontrolled).
  useEffect(() => {
    if (!open || isControlled || !stepDurations?.length || error || internalStep >= total) return;
    const duration = stepDurations[internalStep] ?? 0;
    if (duration <= 0) return;
    const timer = window.setTimeout(() => {
      setInternalStep((s) => s + 1);
      setPulse((p) => p + 1);
    }, duration);
    return () => window.clearTimeout(timer);
  }, [open, isControlled, stepDurations, internalStep, total, error]);

  // Pulse on controlled step change.
  useEffect(() => {
    if (!open || !isControlled) return;
    setPulse((p) => p + 1);
  }, [currentStep, isControlled, open]);

  // Auto progress-mode: fill ring smoothly across total stepDurations.
  useEffect(() => {
    if (!open || resolvedMode !== 'auto' || !stepDurations || error) return;
    const totalDuration = stepDurations.reduce((a, b) => a + b, 0);
    if (totalDuration <= 0) return;
    const startedAt = Date.now();
    let raf = 0;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.min(100, (elapsed / totalDuration) * 100);
      setAutoPercent(pct);
      if (pct < 100) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [open, resolvedMode, stepDurations, error]);

  // Rotate insights.
  useEffect(() => {
    if (!open || insights.length <= 1) return;
    const id = window.setInterval(() => setInsightIndex((i) => (i + 1) % insights.length), insightInterval);
    return () => window.clearInterval(id);
  }, [open, insights.length, insightInterval]);

  // Resolved percent.
  const computedPercent = useMemo(() => {
    if (error) {
      return resolvedMode === 'manual' && typeof percent === 'number'
        ? Math.max(0, Math.min(100, percent))
        : Math.min(100, (error.step / total) * 100);
    }
    if (resolvedMode === 'manual') return Math.max(0, Math.min(100, percent ?? 0));
    if (resolvedMode === 'auto') return autoPercent;
    return Math.min(100, (activeStep / total) * 100);
  }, [error, resolvedMode, percent, autoPercent, activeStep, total]);

  // Fire onComplete once.
  useEffect(() => {
    if (!open || error) return;
    const reached =
      (resolvedMode === 'manual' && computedPercent >= 100) ||
      (resolvedMode === 'stepped' && activeStep >= total) ||
      (resolvedMode === 'auto' && computedPercent >= 100 && activeStep >= total);
    if (reached && !completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [open, error, resolvedMode, computedPercent, activeStep, total, onComplete]);

  const percentDisplay = Math.round(computedPercent);
  const isComplete = !error && percentDisplay >= 100 && activeStep >= total;

  // Throttled aria announcement every 10%.
  const announcedPercent = useMemo(() => {
    const bucket = Math.floor(percentDisplay / 10) * 10;
    if (bucket !== lastAnnouncedRef.current) lastAnnouncedRef.current = bucket;
    return lastAnnouncedRef.current;
  }, [percentDisplay]);

  const resolvedEyebrow = eyebrow ?? deriveEyebrow(title);
  const currentInsight = insights[insightIndex];

  if (!open) return null;

  return (
    <div className="tpm-overlay" style={{ zIndex }} role="presentation">
      <div
        className="tpm-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* ── Left pane: ring + title ─────────────────────── */}
        <div className="tpm-left">
          {resolvedEyebrow && (
            <span className="tpm-eyebrow">{resolvedEyebrow}</span>
          )}

          <ProgressRing
            key={`ring-${pulse}`}
            size={ringSize}
            percent={computedPercent}
            isComplete={isComplete}
            label={ringLabel}
            reducedMotion={reduced}
          />

          <div aria-live="polite" aria-atomic="true" className="tpm-title-wrap">
            <h2 id={titleId} className="tpm-title">
              {renderTitle(title)}
            </h2>
          </div>

          {/* Visually hidden live region for screen readers */}
          <span className="tpm-sr-only" aria-live="polite">
            {`${announcedPercent} percent ${ringLabel.toLowerCase()}`}
          </span>
        </div>

        {/* ── Right pane: steps + insights + cancel ──────── */}
        <div className="tpm-right">
          <PipelineSteps
            steps={steps}
            current={activeStep}
            error={error}
            variant="comfortable"
          />

          {currentInsight && (
            <div
              key={`insight-${insightIndex}`}
              className={`tpm-insight${reduced ? '' : ' tpm-insight--animated'}`}
            >
              <span className="tpm-insight__label">{currentInsight.label}</span>
              <p className="tpm-insight__text">{currentInsight.text}</p>
            </div>
          )}

          {onCancel && (
            <div className="tpm-cancel-row">
              <button className="tpm-cancel-btn" onClick={onCancel}>
                {cancelLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ProgressRing ─────────────────────────────────────────────────────────────

interface ProgressRingProps {
  size: number;
  percent: number;
  isComplete: boolean;
  label: string;
  reducedMotion: boolean;
}

const ProgressRing = ({ size, percent, isComplete, label, reducedMotion }: ProgressRingProps) => {
  const stroke = Math.max(6, Math.round(size * 0.065));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.min(100, Math.max(0, percent)) / 100);
  const percentInt = Math.round(percent);

  return (
    <div
      className={`tpm-ring${reducedMotion ? '' : ' tpm-ring--pulse'}`}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        style={{ transform: 'rotate(-90deg)', display: 'block' }}
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth={stroke}
        />
        {/* Fill */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-primary-500)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{
            transition: reducedMotion ? 'none' : 'stroke-dashoffset 320ms cubic-bezier(0.2, 0, 0, 1)',
          }}
        />
      </svg>

      <div className="tpm-ring__inner">
        {isComplete ? (
          <svg
            width={Math.round(size * 0.32)}
            height={Math.round(size * 0.32)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-primary-500)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <span
            className="tpm-ring__percent"
            style={{ fontSize: Math.round(size * 0.2) }}
          >
            {percentInt}%
          </span>
        )}
        <span className="tpm-ring__label">{label}</span>
      </div>
    </div>
  );
};

export default TaskPipelineModal;
